import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { MenuItem, Select } from '@material-ui/core'
import { TrackChangeStatus } from 'models/changes'
import React from 'react'
import { TrackChangeStatusLabels } from '../../enums/TrackChangeStatusLabels'
import useBetaWorkspaceToggle from '../../hooks/useBetaWorkspaceToggle'
import { useStyles } from '../../TrackChangesPane'

interface TrackChangesFilterProps {
    selectedResolutionType: string
    setSelectedResolutionType: (value: string) => void
    defaultResolutionType: string
}

export const TrackChangesFilter: React.FC<TrackChangesFilterProps> = React.memo(
    ({
        selectedResolutionType,
        setSelectedResolutionType,
        defaultResolutionType,
    }) => {
        const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
        const classes = useStyles({
            isBetaWorkspaceToggleEnabled: isBetaWorkspaceToggleEnabled,
        })

        const handleFilterResolutionTypeChange = React.useCallback(
            (e: React.ChangeEvent<{ value: unknown }>) => {
                const resolutionType = e.target.value as string
                setSelectedResolutionType(resolutionType)
            },
            [setSelectedResolutionType]
        )

        const menuProps = React.useMemo(
            () => ({
                getContentAnchorEl: null,
                anchorOrigin: {
                    vertical: 'bottom' as 'bottom',
                    horizontal: 'left' as 'left',
                },
                transformOrigin: {
                    vertical: 'top' as 'top',
                    horizontal: 'left' as 'left',
                },
                PaperProps: {
                    'aria-label': 'Resolution Type Filter Options',
                },
            }),
            []
        )

        const options = React.useMemo(
            () =>
                Object.values(TrackChangeStatus)
                    .filter(
                        (status) => status !== TrackChangeStatus.OPTIONGROUP
                    )
                    .map((status) => (
                        <MenuItem
                            className={classes.filterOptionItem}
                            key={status}
                            value={status}
                        >
                            {
                                TrackChangeStatusLabels[
                                    status as keyof typeof TrackChangeStatus
                                ]
                            }
                        </MenuItem>
                    )),
            [classes.filterOptionItem]
        )

        return (
            <GridItem
                className={classes.filterContainer}
                data-testid="filterGridItem"
            >
                <Select
                    data-testid="filterDropdown"
                    value={selectedResolutionType}
                    onChange={handleFilterResolutionTypeChange}
                    variant="outlined"
                    className={classes.selectInput}
                    classes={{
                        outlined: classes.selectSelectOutlined,
                    }}
                    aria-label="Resolution Type Filter"
                    title="Resolution Type Filter"
                    MenuProps={menuProps}
                >
                    <MenuItem
                        className={classes.filterOptionItem}
                        value={defaultResolutionType}
                    >
                        {defaultResolutionType}
                    </MenuItem>
                    {options}
                </Select>
            </GridItem>
        )
    }
)
